import React, { useEffect, useState } from "react";
import { Box, useTheme } from "@mui/joy";
import { useLocation } from "react-router-dom";
import Upperbar, { UPPERBAR_HEIGHT_PX } from "./Upperbar";
import OrgSettingsSidebar from "./OrgSettingsSidebar";

interface NavContainerOrgSettingsProps {
  children: React.ReactNode;
}

const useIsMobile = () => {
  const theme = useTheme();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      const xsBreakpoint = theme.breakpoints.values.xs || 600;
      setIsMobile(window.innerWidth < xsBreakpoint);
    };
    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);
    return () => window.removeEventListener("resize", checkIsMobile);
  }, [theme.breakpoints.values.xs]);

  return isMobile;
};

const NavContainerOrgSettings: React.FC<NavContainerOrgSettingsProps> = ({
  children,
}) => {
  const location = useLocation();
  const isMobile = useIsMobile();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOverlayOpen, setIsMobileOverlayOpen] = useState(false);
  const [userCollapsed, setUserCollapsed] = useState(false);
  const sidebarWidth = isMobile ? "0px" : isCollapsed ? "64px" : "250px";

  useEffect(() => {
    if (isMobile) setIsMobileOverlayOpen(false);
  }, [location.pathname, isMobile]);

  useEffect(() => {
    if (isMobile) {
      setIsCollapsed(true);
    } else if (!userCollapsed) {
      setIsCollapsed(false);
    }
  }, [isMobile, userCollapsed]);

  useEffect(() => {
    document.documentElement.style.setProperty(
      "--workspace-sidebar-width",
      sidebarWidth,
    );
    return () => {
      document.documentElement.style.removeProperty("--workspace-sidebar-width");
    };
  }, [sidebarWidth]);

  const toggleSidebar = () => {
    if (isMobile) {
      setIsMobileOverlayOpen(!isMobileOverlayOpen);
    } else {
      const newCollapsed = !isCollapsed;
      setIsCollapsed(newCollapsed);
      setUserCollapsed(newCollapsed);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        boxSizing: "border-box",
        pt: `${UPPERBAR_HEIGHT_PX}px`,
        "--workspace-sidebar-width": sidebarWidth,
      }}
    >
      <Upperbar onToggleSidebar={toggleSidebar} />
      <Box sx={{ display: "flex", flex: 1, position: "relative", minHeight: 0 }}>
        {/* Desktop sidebar */}
        {!isMobile && (
          <OrgSettingsSidebar isCollapsed={isCollapsed} isMobile={false} />
        )}

        {/* Mobile overlay */}
        {isMobile && isMobileOverlayOpen && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1200,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
            }}
            onClick={() => setIsMobileOverlayOpen(false)}
          >
            <Box
              onClick={(e) => e.stopPropagation()}
              sx={{
                width: "280px",
                maxWidth: "85vw",
                height: "100%",
                backgroundColor: "background.surface",
                borderRight: "1px solid",
                borderColor: "divider",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <OrgSettingsSidebar
                isCollapsed={false}
                isMobile={true}
                onClose={() => setIsMobileOverlayOpen(false)}
              />
            </Box>
          </Box>
        )}

        <Box
          component="main"
          key={location.pathname}
          sx={{
            flex: 1,
            backgroundColor: "background.body",
            minWidth: 0,
            minHeight: 0,
            overflowX: "clip",
            overflowY: "auto",
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default NavContainerOrgSettings;
